/// <reference types="vite/client" />

import type { Integration } from '@sentry/core'
import * as Sentry from '@sentry/react'
import { installNonErrorRejectionProbe } from './non-error-rejection-probe'

type InitSentryOpts = {
	dsn: string
	project: string
	applicationKey: string
	releaseName?: string
	network?: string
	environment?: string
	extraIntegrations?: Integration[]
	getIntegrations?: (base: Integration[]) => Integration[]
	enabled?: boolean
}

export function initSentry(opts: InitSentryOpts): void {
	const {
		dsn,
		project,
		applicationKey,
		releaseName,
		network,
		environment,
		extraIntegrations = [],
		getIntegrations,
		enabled = !import.meta.env.DEV,
	} = opts

	if (enabled) {
		const base: Integration[] = [
			Sentry.moduleMetadataIntegration(),
			Sentry.thirdPartyErrorFilterIntegration({
				filterKeys: [applicationKey],
				behaviour: 'drop-error-if-contains-third-party-frames',
			}),
		]

		// Apps that supply getIntegrations add their own browser-tracing variant
		// (e.g. reactRouterV6BrowserTracingIntegration). The generic fallback is
		// only included when no getIntegrations callback is provided.
		const integrations = getIntegrations
			? getIntegrations(base)
			: [Sentry.browserTracingIntegration(), ...base, ...extraIntegrations]

		// Gate distributed trace header injection behind ?trace=1.
		// HTTP timing spans are always collected (traceFetch defaults to true above),
		// but sentry-trace/baggage headers are only injected when explicitly enabled.
		// This avoids breaking endpoints that redirect to cross-origin (CORS) targets.
		const traceEnabled =
			new URLSearchParams(window.location.search).get('trace') === '1'

		// Sentry matches tracePropagationTargets against the full resolved URL
		// (e.g. "https://snatch.peak226.com/api/v2/..."), not the relative path.
		// Match same-origin requests only.
		const { origin } = window.location

		Sentry.init({
			dsn,
			dataCollection: { userInfo: true },
			environment:
				environment ?? (import.meta.env.DEV ? 'development' : 'production'),
			denyUrls: [
				/chrome-extension:\/\//,
				/moz-extension:\/\//,
				/safari-extension:\/\//,
			],
			integrations,
			// Only collect performance traces when explicitly opted in via ?trace=1.
			tracesSampleRate: traceEnabled ? 1 : 0,
			tracePropagationTargets: traceEnabled ? [origin] : [],
			replaysSessionSampleRate: 0,
			replaysOnErrorSampleRate: 1,
			...(releaseName ? { release: releaseName } : {}),
			initialScope: {
				tags: {
					...(network ? { network } : {}),
					project,
				},
			},
		})
	}

	installNonErrorRejectionProbe()
}
